import {HTMLReactParserOptions, Element as ParserElement, DOMNode, domToReact} from 'html-react-parser';
import {useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ClientDynamicContent, NodeElement, UserPreview} from 'components';
import {DynamicComponentNames, CallingUnits as Units, CALLING_DEFAULT_TEMPLATE} from 'constants/client';
import {ClientContext} from 'context';
import {useClientSession} from 'hooks';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {dynamicContentUtils} from 'utils';
import {getParticipantName} from 'utils/transform';

import {LeaveSessionButton} from '../LeaveSessionButton';
import {WaitingAgentResponse} from '../WaitingAgentResponse';

import './styles.scss';

const T_KEYS_CLIENT_IN_SESSION_PAGE = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE;
const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.CALLING_VIEW;

const {isMatchElement, nodeElementParent, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {
        description, endLocalSession, leaveSessionLabel, agentName, yourAgentLabel, reassignedToAllFrom, reassignedToAllLabel,
        localParticipant
    } = props;

    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;
            const showAgentData = !!agentName;

            if (isMatchElement(Units.AgentInfo, node)) {
                return nodeElementParent({show: showAgentData});
            }
            if (isMatchElement(Units.YourAgentLabel, node)) {
                return <NodeElement show={showAgentData} node={node}>{yourAgentLabel}</NodeElement>;
            }
            if (isMatchElement(Units.AgentPreview, node)) {
                return <NodeElement show={showAgentData} node={node}><UserPreview name={agentName} /></NodeElement>;
            }
            if (isMatchElement(Units.AgentName, node)) {
                return <NodeElement show={showAgentData} node={node}>{agentName}</NodeElement>;
            }

            if (isMatchElement(Units.Icon, node)) {
                return <NodeElement node={node}><WaitingAgentResponse /></NodeElement>;
            }
            if (isMatchElement(Units.ReassignedToAll, node)) {
                return (
                    <NodeElement node={node} show={!!reassignedToAllFrom}>
                        {node.children?.length ? domToReact(node.children as DOMNode[]) : reassignedToAllLabel}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.Description, node)) {
                return <NodeElement node={node}>{description}</NodeElement>;
            }
            if (isMatchElement(Units.LeaveSessionButton, node)) {
                return (
                    <NodeElement node={node} onClick={endLocalSession}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.LeaveSessionButtonDefault, node)) {
                return (
                    <NodeElement node={node}>
                        <LeaveSessionButton onClick={endLocalSession} />
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.LeaveSessionButtonLabel, node)) {
                return (
                    <NodeElement node={node}>{leaveSessionLabel}</NodeElement>
                );
            }
            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

export const CallingView: React.FC = () => {
    const {t} = useTranslation();
    const {endLocalSession} = useContext(ClientContext);
    const {image, agent, session} = useClientSession();
    const yourAgentLabel = useMemo(() => t(T_KEYS_CLIENT_IN_SESSION_PAGE.YOUR_AGENT_IS), [t]);
    const agentName = useMemo(() => getParticipantName(agent?.data || session?.reassignedToAllFrom), [agent, session]);
    const reassignedToAllLabel = useMemo(() => t(T_KEYS_CLIENT_IN_SESSION_PAGE.REASSIGNED_TO_ALL), [t]);

    return (
        <ClientDynamicLayout image={image}>
            <ClientDynamicContent
                component={DynamicComponentNames.Calling}
                defaultTemplate={CALLING_DEFAULT_TEMPLATE}
                optionsProps={{
                    agentName,
                    yourAgentLabel,
                    reassignedToAllFrom: session?.reassignedToAllFrom,
                    reassignedToAllLabel,
                    description: t(T_KEYS.CALLING),
                    leaveSessionLabel: t(T_KEYS_CLIENT_IN_SESSION_PAGE.END_CALL),
                    endLocalSession
                }}
                optionsGetter={getOptions}
            />
        </ClientDynamicLayout>
    );
};
